import React from 'react';
import {
  Form,
  useActionData,
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
  useOutlet,
  useParams,
  useRevalidator,
  useSubmit
} from 'react-router-dom';
import * as Headless from '@headlessui/react';
import { Await } from '@oysterjs/ui/async';
import { AnimatePresence, motion } from 'motion/react';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';

import {
  AcordFormConfigurationFieldsFragment,
  LoggedInAgencyUserFieldsFragment,
  UpdateAgencyMutation,
  ProducerInformationFieldsFragment,
  ProducerInformationInput,
  CreateAgencyProducerInformationMutation,
  UpdateProducerInformationMutation,
  UpdateProducerInformationMutationVariables,
  UpdateProducerInformationDocument,
  Resource,
  Action
} from '~/api/graphql';
import { Spinner } from '@oysterjs/ui/spinner';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { ValidationError } from '@oysterjs/core/graphql';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { useFormActions } from '@oysterjs/core/graphql/form';
import {
  getCaraTriggerEmailAddress,
  sanitizeAgencyEmailNamespaceInput
} from '@oysterjs/core/email';

import { Badge } from '@oysterjs/ui/badge';
import { TextLink } from '@oysterjs/ui/text';
import { Button } from '@oysterjs/ui/button';
import { MountableSlideout } from '@oysterjs/ui/slideout';
import { Checkbox, CheckboxField } from '@oysterjs/ui/checkbox';
import { Radio, RadioField, RadioGroup } from '@oysterjs/ui/radio';
import { SignatureCanvas } from '@oysterjs/ui/signature';
import { client } from '~/api/client';
import { usePermissions } from '~/components/permissions';
import { COIEOISection, COIEOISectionHandle } from '~/components/settings/coi_eoi_section';
import {
  pendingChangesSaveMessages,
  PendingChangesSaveBar,
  PendingChangesSaveToast,
  usePendingChangesSave
} from '~/components/settings/pending-changes-save';

interface AgencyFormState {
  name: string;
  displayName: string;
  emailNamespace: string;
  phone: string;
  nationalProducerNumber: string;
  floridaLicenseNumber: string;
  mailingAddress: {
    line1: string;
    line2: string;
    city: string;
    subZone: string;
    zone: string;
    postalCode: string;
  };
  disallowPrivate: boolean;
}

const AgencyPageLoader = () => {
  return (
    <div className="px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
          Loading agency information...
        </h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          This might take a moment.
        </p>
      </div>
    </div>
  );
};

export const SettingsAgencyPage = () => {
  const outlet = useOutlet();
  const loader = useLoaderData() as {
    data: Promise<{
      agencyUser: LoggedInAgencyUserFieldsFragment;
      producerInformations: ProducerInformationFieldsFragment[];
      defaultAcordFormConfiguration: AcordFormConfigurationFieldsFragment;
    }>;
  };

  // The COI/EOI section is a sibling of the agency form but shares the single
  // page-level save bar: it reports its dirty state here and exposes its input so
  // the agency form folds it into one updateAgency call (avoiding a second save bar
  // and a concurrent second mutation that could clobber the agency record).
  const coiRef = React.useRef<COIEOISectionHandle>(null);
  const [coiDirty, setCoiDirty] = React.useState(false);

  return (
    <React.Suspense fallback={<AgencyPageLoader />}>
      <Await resolve={loader.data}>
        {(data) => {
          if (!data?.agencyUser?.agency) {
            return null;
          }
          return (
            <div className="space-y-6">
              <AgencySettingsForm
                agency={data.agencyUser.agency}
                coiRef={coiRef}
                coiDirty={coiDirty}
              />
              <ProducerInformationSection producerInformations={data.producerInformations ?? []} />
              <COIEOISection
                ref={coiRef}
                agency={data.agencyUser.agency}
                defaultConfiguration={data.defaultAcordFormConfiguration}
                onDirtyChange={setCoiDirty}
              />
              <AnimatePresence mode="wait">{outlet && React.cloneElement(outlet)}</AnimatePresence>
            </div>
          );
        }}
      </Await>
    </React.Suspense>
  );
};

const AgencySettingsForm = (props: {
  agency: LoggedInAgencyUserFieldsFragment['agency'];
  // The COI/EOI section shares this form's save bar: its dirty state drives bar
  // visibility, and its input is folded into this form's single updateAgency call.
  coiRef: React.RefObject<COIEOISectionHandle | null>;
  coiDirty: boolean;
}) => {
  const action = useActionData() as ActionResult<UpdateAgencyMutation>;
  const submit = useSubmit();
  const persistedPrivacyMode = props.agency?.configuration?.hipaaPrivacyMode?.enabled ?? false;

  const initialForm: AgencyFormState = React.useMemo(
    () => ({
      name: props.agency?.name ?? '',
      displayName: props.agency?.displayName ?? '',
      emailNamespace: props.agency?.emailNamespace ?? '',
      phone: props.agency?.phone ?? '',
      nationalProducerNumber: props.agency?.nationalProducerNumber ?? '',
      floridaLicenseNumber: props.agency?.floridaLicenseNumber ?? '',
      mailingAddress: {
        line1: props.agency?.mailingAddress?.line1 ?? '',
        line2: props.agency?.mailingAddress?.line2 ?? '',
        city: props.agency?.mailingAddress?.city ?? '',
        subZone: props.agency?.mailingAddress?.subZone ?? '',
        zone: props.agency?.mailingAddress?.zone ?? '',
        postalCode: props.agency?.mailingAddress?.postalCode ?? ''
      },
      disallowPrivate: props.agency?.configuration?.visibility?.disallowPrivate ?? false
    }),
    []
  );

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<AgencyFormState>(initialForm);
  const { getValidationError } = useFormActions(setForm, setValidationErrors);

  const { changesPending, saved, error, discardChanges, markPendingChanges } =
    usePendingChangesSave({
      initialSavedState: initialForm,
      action,
      saveSuccessTrigger: action?.data?.updateAgency,
      isSaveSuccessful: (saveAction) => !!saveAction?.data?.updateAgency?.id,
      getSavedStateAfterCommit: () => form,
      onAfterSave: () => {
        // Clear the COI/EOI section's dirty flag too, since it saved in the same call.
        props.coiRef.current?.markSaved(
          action?.data?.updateAgency?.configuration?.acordFormConfiguration
        );
      },
      clearPendingOn: 'idle'
    });

  const isInitialized = React.useRef(false);

  const updateField = <K extends keyof AgencyFormState>(field: K, value: AgencyFormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    markPendingChanges();
  };

  const updateMailingAddressField = <K extends keyof AgencyFormState['mailingAddress']>(
    field: K,
    value: string
  ) => {
    setForm((prev) => ({
      ...prev,
      mailingAddress: { ...prev.mailingAddress, [field]: value }
    }));
    markPendingChanges();
  };

  React.useEffect(() => {
    setValidationErrors(action?.validationErrors || []);
  }, [action?.validationErrors]);

  React.useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;
    setForm(initialForm);
  }, [initialForm]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Block the save if the COI/EOI section has invalid edits; it surfaces its own
    // inline errors. Only the dirty sections are included in the single call.
    if (props.coiDirty && props.coiRef.current && !props.coiRef.current.validate()) {
      return;
    }
    const input = {
      name: form.name,
      displayName: form.displayName,
      emailNamespace: form.emailNamespace,
      phone: form.phone,
      nationalProducerNumber: form.nationalProducerNumber,
      floridaLicenseNumber: form.floridaLicenseNumber,
      mailingAddress: {
        line1: form.mailingAddress.line1,
        line2: form.mailingAddress.line2 || null,
        subZone: form.mailingAddress.subZone || null,
        city: form.mailingAddress.city,
        zone: form.mailingAddress.zone,
        postalCode: form.mailingAddress.postalCode
      },
      visibility: { disallowPrivate: form.disallowPrivate },
      // Fold the COI/EOI config into the same updateAgency call (the action forwards
      // the whole input), so both sections persist atomically with one mutation.
      ...(props.coiDirty && props.coiRef.current
        ? { acordFormConfiguration: props.coiRef.current.getInput() }
        : {})
    };
    submit(input, { method: 'PUT', encType: 'application/json' });
  };

  return (
    <Form className="space-y-6" onSubmit={handleSubmit}>
      <PendingChangesSaveToast
        saved={saved}
        error={error}
        messages={pendingChangesSaveMessages.agency}
      />
      <PendingChangesSaveBar
        changesPending={changesPending || props.coiDirty}
        onDiscard={() => {
          discardChanges(setForm);
          // Discard the COI/EOI section's edits too, since they share this bar.
          props.coiRef.current?.discard();
        }}
      />

      <div className="rounded-lg bg-white dark:bg-neutral-800 shadow">
        <div className="px-4 py-6 sm:px-6">
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
            <div>
              <h4 className="mt-0 font-body font-semibold text-neutral-900 dark:text-white">
                Basic Information
              </h4>
              <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
                Update your agency's basic information.
              </p>
            </div>

            <Fieldset className="flex flex-col gap-y-6 col-span-2">
              <FieldGroup>
                <Field className="max-w-md">
                  <Label>Agency Name</Label>
                  <Description>The legal name of your agency.</Description>
                  <Input
                    value={form.name}
                    onChange={(e) => updateField('name', e.currentTarget.value)}
                    placeholder="Acme Insurance Agency"
                    invalid={!!getValidationError(validationErrors, ['name'])}
                  />
                  {getValidationError(validationErrors, ['name']) && (
                    <ErrorMessage>{getValidationError(validationErrors, ['name'])}</ErrorMessage>
                  )}
                </Field>

                <Field className="max-w-md">
                  <Label>Display Name</Label>
                  <Description>
                    A friendly name shown to customers and in the application.
                  </Description>
                  <Input
                    value={form.displayName}
                    onChange={(e) => updateField('displayName', e.currentTarget.value)}
                    placeholder="Acme Insurance"
                    invalid={!!getValidationError(validationErrors, ['displayName'])}
                  />
                  {getValidationError(validationErrors, ['displayName']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['displayName'])}
                    </ErrorMessage>
                  )}
                </Field>

                <Field className="max-w-md">
                  <Label>Email handle</Label>
                  <Description>
                    Short unique handle for Cara trigger aliases. Triggers combine this handle with
                    a suffix you choose per workflow; the full inbox address is shown below.
                  </Description>
                  <Input
                    value={form.emailNamespace}
                    onChange={(e) =>
                      updateField(
                        'emailNamespace',
                        sanitizeAgencyEmailNamespaceInput(e.currentTarget.value)
                      )
                    }
                    placeholder="acme"
                    invalid={!!getValidationError(validationErrors, ['emailNamespace'])}
                  />
                  {form.emailNamespace ? (
                    <p className="mt-2 font-mono text-xs text-neutral-500 dark:text-neutral-400">
                      {getCaraTriggerEmailAddress(form.emailNamespace, 'coi')}
                    </p>
                  ) : null}
                  {getValidationError(validationErrors, ['emailNamespace']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['emailNamespace'])}
                    </ErrorMessage>
                  )}
                </Field>

                <Field className="max-w-md">
                  <Label>Phone Number</Label>
                  <Description>Your agency's primary contact phone number.</Description>
                  <Input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => updateField('phone', e.currentTarget.value)}
                    placeholder="(555) 123-4567"
                    invalid={!!getValidationError(validationErrors, ['phone'])}
                  />
                  {getValidationError(validationErrors, ['phone']) && (
                    <ErrorMessage>{getValidationError(validationErrors, ['phone'])}</ErrorMessage>
                  )}
                </Field>

                <Field className="max-w-md">
                  <Label>National Producer Number (NPN)</Label>
                  <Description>
                    Your agency's NPN. Used only as a backup when the selected default producer
                    information has none.
                  </Description>
                  <Input
                    value={form.nationalProducerNumber}
                    onChange={(e) => updateField('nationalProducerNumber', e.currentTarget.value)}
                    placeholder="12345678"
                    invalid={!!getValidationError(validationErrors, ['nationalProducerNumber'])}
                  />
                  {getValidationError(validationErrors, ['nationalProducerNumber']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['nationalProducerNumber'])}
                    </ErrorMessage>
                  )}
                </Field>

                <Field className="max-w-md">
                  <Label>FL License Number</Label>
                  <Description>
                    Your agency's Florida producer license number. Used only as a backup when the
                    selected default producer information has none, and printed on ACORD forms when
                    the risk is in Florida (or the form is Florida-specific).
                  </Description>
                  <Input
                    value={form.floridaLicenseNumber}
                    onChange={(e) => updateField('floridaLicenseNumber', e.currentTarget.value)}
                    placeholder="A123456"
                    invalid={!!getValidationError(validationErrors, ['floridaLicenseNumber'])}
                  />
                  {getValidationError(validationErrors, ['floridaLicenseNumber']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['floridaLicenseNumber'])}
                    </ErrorMessage>
                  )}
                </Field>

                <Field className="max-w-md">
                  <Label>Mailing Address</Label>
                  <Description>The mailing address of your agency.</Description>
                  <Input
                    value={form.mailingAddress.line1}
                    onChange={(e) => updateMailingAddressField('line1', e.currentTarget.value)}
                    placeholder="123 Main St"
                    invalid={!!getValidationError(validationErrors, ['mailingAddress', 'line1'])}
                  />
                  {getValidationError(validationErrors, ['mailingAddress', 'line1']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['mailingAddress', 'line1'])}
                    </ErrorMessage>
                  )}
                </Field>

                <Field className="max-w-md">
                  <Label>Address Line 2</Label>
                  <Input
                    value={form.mailingAddress.line2}
                    onChange={(e) => updateMailingAddressField('line2', e.currentTarget.value)}
                    placeholder="Suite 100"
                    invalid={!!getValidationError(validationErrors, ['mailingAddress', 'line2'])}
                  />
                  {getValidationError(validationErrors, ['mailingAddress', 'line2']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['mailingAddress', 'line2'])}
                    </ErrorMessage>
                  )}
                </Field>

                <div className="max-w-md flex flex-row gap-4">
                  <Field className="flex-grow">
                    <Label>City</Label>
                    <Input
                      value={form.mailingAddress.city}
                      onChange={(e) => updateMailingAddressField('city', e.currentTarget.value)}
                      placeholder="San Francisco"
                      invalid={!!getValidationError(validationErrors, ['mailingAddress', 'city'])}
                    />
                    {getValidationError(validationErrors, ['mailingAddress', 'city']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['mailingAddress', 'city'])}
                      </ErrorMessage>
                    )}
                  </Field>

                  <Field className="max-w-20">
                    <Label>State</Label>
                    <Input
                      value={form.mailingAddress.zone}
                      onChange={(e) => updateMailingAddressField('zone', e.currentTarget.value)}
                      placeholder="CA"
                      invalid={!!getValidationError(validationErrors, ['mailingAddress', 'zone'])}
                    />
                    {getValidationError(validationErrors, ['mailingAddress', 'zone']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['mailingAddress', 'zone'])}
                      </ErrorMessage>
                    )}
                  </Field>

                  <Field className="max-w-24">
                    <Label>Postal Code</Label>
                    <Input
                      value={form.mailingAddress.postalCode}
                      onChange={(e) =>
                        updateMailingAddressField('postalCode', e.currentTarget.value)
                      }
                      placeholder="94102"
                      invalid={
                        !!getValidationError(validationErrors, ['mailingAddress', 'postalCode'])
                      }
                    />
                    {getValidationError(validationErrors, ['mailingAddress', 'postalCode']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['mailingAddress', 'postalCode'])}
                      </ErrorMessage>
                    )}
                  </Field>
                </div>
              </FieldGroup>
            </Fieldset>
          </div>
        </div>
      </div>

      <div className="rounded-lg bg-white dark:bg-neutral-800 shadow">
        <div className="px-4 py-6 sm:px-6">
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
            <div>
              <h4 className="mt-0 font-body font-semibold text-neutral-900 dark:text-white">
                HIPAA privacy mode
              </h4>
              <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
                {persistedPrivacyMode ? (
                  'Refer to your Business Associate Agreement (BAA) for details.'
                ) : (
                  <TextLink
                    href="https://meetings.hubspot.com/vic-yeh"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Contact sales to enable. (opens in a new tab)"
                  >
                    Contact sales to enable.
                  </TextLink>
                )}
              </p>
            </div>
            <Fieldset
              className="flex flex-col gap-y-6 md:col-span-2"
              aria-label="HIPAA privacy mode"
            >
              <div className="flex items-center gap-3">
                <Badge
                  color={persistedPrivacyMode ? 'green' : 'zinc'}
                  role="status"
                  aria-label="HIPAA privacy mode"
                >
                  {persistedPrivacyMode ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
            </Fieldset>
          </div>
        </div>
      </div>

      <div className="rounded-lg bg-white dark:bg-neutral-800 shadow">
        <div className="px-4 py-6 sm:px-6">
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
            <div>
              <h4 className="mt-0 font-body font-semibold text-neutral-900 dark:text-white">
                Visibility policy
              </h4>
              <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
                Constrain the visibility levels available to members of your agency.
              </p>
            </div>
            <Fieldset className="flex flex-col gap-y-6 col-span-2">
              <FieldGroup>
                <CheckboxField>
                  <Checkbox
                    color="sky"
                    checked={form.disallowPrivate}
                    onChange={(checked) => updateField('disallowPrivate', checked)}
                  />
                  <Label>Disallow private items</Label>
                  <Description>
                    When enabled, members can&apos;t set visibility to Private — they must choose at
                    least Shared. Existing private items stay private until edited.
                  </Description>
                </CheckboxField>
              </FieldGroup>
            </Fieldset>
          </div>
        </div>
      </div>
    </Form>
  );
};

// ─── Producer Information Section ────────────────────────────────────────────

const ProducerInformationSection = ({
  producerInformations
}: {
  producerInformations: ProducerInformationFieldsFragment[];
}) => {
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const { hasPermission } = usePermissions();

  const canWrite = hasPermission(Resource.ProducerInformation, Action.Create);

  const handleMakeDefault = async (pi: ProducerInformationFieldsFragment) => {
    await client.mutate<
      UpdateProducerInformationMutation,
      UpdateProducerInformationMutationVariables
    >({
      mutation: UpdateProducerInformationDocument,
      variables: {
        id: pi.id,
        input: producerInformationToUpdateInput(pi, { isDefault: true })
      }
    });
    revalidator.revalidate();
  };

  const sortedProducerInformations = [...producerInformations].sort((a, b) => {
    if (a.isDefault && !b.isDefault) return -1;
    if (!a.isDefault && b.isDefault) return 1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  return (
    <div className="divide-y divide-neutral-200 dark:divide-neutral-700 rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="px-4 py-5 sm:px-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
            Producer Information
          </h3>
          {canWrite && (
            <Button color="sky" href="/app/settings/agency/producer-information/new">
              Create
            </Button>
          )}
        </div>
      </div>
      <div className="py-5">
        {sortedProducerInformations.length === 0 ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-4 px-4 sm:px-6">
            No producer information configured. Create one to use in certificates and documents.
          </p>
        ) : (
          <ul role="list">
            {sortedProducerInformations.map((pi) => (
              <motion.li
                key={pi.id}
                layout
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                className="flex justify-between px-4 py-4 sm:px-6 border-b border-neutral-200 dark:border-neutral-700 last:border-b-0 first:pt-0 last:pb-0"
              >
                <div className="flex min-w-0 gap-x-4">
                  <div className="size-12 flex-none rounded-full bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center text-neutral-700 dark:text-neutral-300 font-semibold text-lg">
                    {pi.producerName
                      .split(' ')
                      .map((w) => w[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2)}
                  </div>
                  <div className="min-w-0 flex-auto">
                    <p className="text-sm/6 font-semibold text-neutral-900 dark:text-white">
                      {pi.producerName}
                      {pi.isDefault && (
                        <span className="ml-2">
                          <Badge color="sky">Default</Badge>
                        </span>
                      )}
                    </p>
                    <p className="flex text-xs/5 text-neutral-500 dark:text-neutral-400">
                      {pi.agencyName}
                    </p>
                  </div>
                </div>
                {canWrite && (
                  <div className="flex shrink-0 items-center gap-x-6">
                    <Headless.Menu as="div" className="relative flex-none">
                      <Headless.MenuButton className="-m-2.5 block p-2.5 text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white">
                        <span className="sr-only">Open options</span>
                        <EllipsisVerticalIcon aria-hidden="true" className="size-5" />
                      </Headless.MenuButton>
                      <Headless.MenuItems
                        transition
                        className="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-white dark:bg-neutral-800 py-2 shadow-lg ring-1 ring-neutral-900/5 dark:ring-white/10 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                      >
                        {!pi.isDefault && (
                          <Headless.MenuItem>
                            <span
                              role="button"
                              onClick={() => handleMakeDefault(pi)}
                              className="block px-3 py-1 text-sm/6 text-neutral-900 dark:text-white data-[focus]:bg-neutral-50 dark:data-[focus]:bg-white/5 data-[focus]:outline-none cursor-pointer"
                            >
                              Make default
                            </span>
                          </Headless.MenuItem>
                        )}
                        <Headless.MenuItem>
                          <span
                            role="button"
                            onClick={() =>
                              navigate(`/app/settings/agency/producer-information/${pi.id}/edit`)
                            }
                            className="block px-3 py-1 text-sm/6 text-neutral-900 dark:text-white data-[focus]:bg-neutral-50 dark:data-[focus]:bg-white/5 data-[focus]:outline-none cursor-pointer"
                          >
                            Edit
                          </span>
                        </Headless.MenuItem>
                      </Headless.MenuItems>
                    </Headless.Menu>
                  </div>
                )}
              </motion.li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ─── Producer Information Form ────────────────────────────────────────────────

export interface ProducerInfoFormState {
  isDefault: boolean;
  agencyName: string;
  agencyAddress: {
    line1: string;
    line2: string;
    city: string;
    zone: string;
    postalCode: string;
  };
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  contactFax: string;
  producerName: string;
  nationalProducerNumber: string;
  floridaLicenseNumber: string;
  producerSignatureJpegBase64: string;
}

export const emptyForm = (): ProducerInfoFormState => ({
  isDefault: false,
  agencyName: '',
  agencyAddress: { line1: '', line2: '', city: '', zone: '', postalCode: '' },
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  contactFax: '',
  producerName: '',
  nationalProducerNumber: '',
  floridaLicenseNumber: '',
  producerSignatureJpegBase64: ''
});

export const piToForm = (pi: ProducerInformationFieldsFragment): ProducerInfoFormState => ({
  isDefault: pi.isDefault,
  agencyName: pi.agencyName,
  agencyAddress: {
    line1: pi.agencyAddress.line1,
    line2: pi.agencyAddress.line2 ?? '',
    city: pi.agencyAddress.city,
    zone: pi.agencyAddress.zone,
    postalCode: pi.agencyAddress.postalCode
  },
  contactName: pi.contactName,
  contactEmail: pi.contactEmail,
  contactPhone: pi.contactPhone,
  contactFax: pi.contactFax,
  producerName: pi.producerName,
  nationalProducerNumber: pi.nationalProducerNumber,
  floridaLicenseNumber: pi.floridaLicenseNumber,
  producerSignatureJpegBase64: pi.producerSignatureJpegBase64
});

export const formToInput = (form: ProducerInfoFormState): ProducerInformationInput => ({
  isDefault: form.isDefault,
  agencyName: form.agencyName,
  agencyAddress: {
    line1: form.agencyAddress.line1,
    line2: form.agencyAddress.line2 || undefined,
    city: form.agencyAddress.city,
    zone: form.agencyAddress.zone,
    postalCode: form.agencyAddress.postalCode
  },
  contactName: form.contactName,
  contactEmail: form.contactEmail,
  contactPhone: form.contactPhone,
  contactFax: form.contactFax,
  producerName: form.producerName,
  nationalProducerNumber: form.nationalProducerNumber,
  floridaLicenseNumber: form.floridaLicenseNumber,
  producerSignatureJpegBase64: form.producerSignatureJpegBase64
});

export const producerInformationToUpdateInput = (
  pi: ProducerInformationFieldsFragment,
  overrides: Partial<ProducerInformationInput> = {}
): ProducerInformationInput => ({
  ...formToInput(piToForm(pi)),
  ...overrides
});

export const ProducerInformationForm = ({
  initialValues,
  existingSignatureIsDrawn,
  onSubmit,
  onClose,
  loading,
  error,
  validationErrors: externalValidationErrors = []
}: {
  initialValues: ProducerInfoFormState;
  existingSignatureIsDrawn: boolean;
  onSubmit: (input: ProducerInformationInput) => void;
  onClose: () => void;
  loading: boolean;
  error: string | null;
  validationErrors?: ValidationError[];
}) => {
  const [form, setForm] = React.useState<ProducerInfoFormState>(initialValues);
  const [validationErrors, setValidationErrors] =
    React.useState<ValidationError[]>(externalValidationErrors);
  const [signatureMode, setSignatureMode] = React.useState<'text' | 'draw'>(
    existingSignatureIsDrawn ? 'draw' : 'text'
  );
  const [showCanvas, setShowCanvas] = React.useState(false);

  React.useEffect(() => {
    setValidationErrors(externalValidationErrors);
  }, [externalValidationErrors]);

  const { updateForm, getValidationError: getFieldError } = useFormActions(
    setForm,
    setValidationErrors
  );

  const hasDrawnSignature = !!form.producerSignatureJpegBase64 && signatureMode === 'draw';

  const handleSignatureModeChange = (mode: 'text' | 'draw') => {
    setSignatureMode(mode);
    if (mode === 'text') {
      setForm((prev) => ({ ...prev, producerSignatureJpegBase64: '' }));
      setShowCanvas(false);
    }
  };

  const handleSignatureSave = (jpegBase64: string) => {
    setForm((prev) => ({ ...prev, producerSignatureJpegBase64: jpegBase64 }));
    setShowCanvas(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formToInput(form));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Fieldset>
        <FieldGroup>
          <CheckboxField>
            <Label>Set as default</Label>
            <Description>
              This producer information will be used by default in new certificates and documents.
            </Description>
            <Checkbox
              color="sky"
              checked={form.isDefault}
              onChange={(checked) => setForm((prev) => ({ ...prev, isDefault: checked }))}
            />
          </CheckboxField>
        </FieldGroup>
      </Fieldset>

      <Fieldset>
        <FieldGroup>
          <Field>
            <Label>Agency Name</Label>
            <Description>The name of the agency as it appears on documents.</Description>
            <Input
              value={form.agencyName}
              onChange={(e) => {
                const v = e.currentTarget.value;
                updateForm(['agencyName'], (prev) => ({ ...prev, agencyName: v }));
              }}
              placeholder="Acme Insurance Agency"
              invalid={!!getFieldError(validationErrors, ['agencyName'])}
            />
            {getFieldError(validationErrors, ['agencyName']) && (
              <ErrorMessage>{getFieldError(validationErrors, ['agencyName'])}</ErrorMessage>
            )}
          </Field>
        </FieldGroup>

        <FieldGroup>
          <Field>
            <Label>Agency Address</Label>
            <Description>The address of the agency as it appears on documents.</Description>
            <Input
              value={form.agencyAddress.line1}
              onChange={(e) => {
                const v = e.currentTarget.value;
                updateForm(['agencyAddress', 'line1'], (prev) => ({
                  ...prev,
                  agencyAddress: { ...prev.agencyAddress, line1: v }
                }));
              }}
              placeholder="123 Main St"
              invalid={!!getFieldError(validationErrors, ['agencyAddress', 'line1'])}
            />
            {getFieldError(validationErrors, ['agencyAddress', 'line1']) && (
              <ErrorMessage>
                {getFieldError(validationErrors, ['agencyAddress', 'line1'])}
              </ErrorMessage>
            )}
          </Field>
          <Field>
            <Label>Address Line 2</Label>
            <Input
              value={form.agencyAddress.line2}
              onChange={(e) => {
                const v = e.currentTarget.value;
                updateForm(['agencyAddress', 'line2'], (prev) => ({
                  ...prev,
                  agencyAddress: { ...prev.agencyAddress, line2: v }
                }));
              }}
              placeholder="Suite 100"
              invalid={!!getFieldError(validationErrors, ['agencyAddress', 'line2'])}
            />
            {getFieldError(validationErrors, ['agencyAddress', 'line2']) && (
              <ErrorMessage>
                {getFieldError(validationErrors, ['agencyAddress', 'line2'])}
              </ErrorMessage>
            )}
          </Field>
          <div className="flex flex-row gap-4">
            <Field className="flex-grow">
              <Label>City</Label>
              <Input
                value={form.agencyAddress.city}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  updateForm(['agencyAddress', 'city'], (prev) => ({
                    ...prev,
                    agencyAddress: { ...prev.agencyAddress, city: v }
                  }));
                }}
                placeholder="San Francisco"
                invalid={!!getFieldError(validationErrors, ['agencyAddress', 'city'])}
              />
              {getFieldError(validationErrors, ['agencyAddress', 'city']) && (
                <ErrorMessage>
                  {getFieldError(validationErrors, ['agencyAddress', 'city'])}
                </ErrorMessage>
              )}
            </Field>
            <Field className="max-w-20">
              <Label>State</Label>
              <Input
                value={form.agencyAddress.zone}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  updateForm(['agencyAddress', 'zone'], (prev) => ({
                    ...prev,
                    agencyAddress: { ...prev.agencyAddress, zone: v }
                  }));
                }}
                placeholder="CA"
                invalid={!!getFieldError(validationErrors, ['agencyAddress', 'zone'])}
              />
              {getFieldError(validationErrors, ['agencyAddress', 'zone']) && (
                <ErrorMessage>
                  {getFieldError(validationErrors, ['agencyAddress', 'zone'])}
                </ErrorMessage>
              )}
            </Field>
            <Field className="max-w-24">
              <Label>Postal Code</Label>
              <Input
                value={form.agencyAddress.postalCode}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  updateForm(['agencyAddress', 'postalCode'], (prev) => ({
                    ...prev,
                    agencyAddress: { ...prev.agencyAddress, postalCode: v }
                  }));
                }}
                placeholder="94102"
                invalid={!!getFieldError(validationErrors, ['agencyAddress', 'postalCode'])}
              />
              {getFieldError(validationErrors, ['agencyAddress', 'postalCode']) && (
                <ErrorMessage>
                  {getFieldError(validationErrors, ['agencyAddress', 'postalCode'])}
                </ErrorMessage>
              )}
            </Field>
          </div>
        </FieldGroup>
      </Fieldset>

      <Fieldset>
        <FieldGroup>
          <Field>
            <Label>Contact Name</Label>
            <Input
              value={form.contactName}
              onChange={(e) => {
                const v = e.currentTarget.value;
                updateForm(['contactName'], (prev) => ({ ...prev, contactName: v }));
              }}
              placeholder="Jane Smith"
              invalid={!!getFieldError(validationErrors, ['contactName'])}
            />
            {getFieldError(validationErrors, ['contactName']) && (
              <ErrorMessage>{getFieldError(validationErrors, ['contactName'])}</ErrorMessage>
            )}
          </Field>
          <Field>
            <Label>Contact Email</Label>
            <Input
              type="email"
              value={form.contactEmail}
              onChange={(e) => {
                const v = e.currentTarget.value;
                updateForm(['contactEmail'], (prev) => ({ ...prev, contactEmail: v }));
              }}
              placeholder="jane@acme.com"
              invalid={!!getFieldError(validationErrors, ['contactEmail'])}
            />
            {getFieldError(validationErrors, ['contactEmail']) && (
              <ErrorMessage>{getFieldError(validationErrors, ['contactEmail'])}</ErrorMessage>
            )}
          </Field>
          <Field>
            <Label>Contact Phone</Label>
            <Input
              type="tel"
              value={form.contactPhone}
              onChange={(e) => {
                const v = e.currentTarget.value;
                updateForm(['contactPhone'], (prev) => ({ ...prev, contactPhone: v }));
              }}
              placeholder="(555) 123-4567"
              invalid={!!getFieldError(validationErrors, ['contactPhone'])}
            />
            {getFieldError(validationErrors, ['contactPhone']) && (
              <ErrorMessage>{getFieldError(validationErrors, ['contactPhone'])}</ErrorMessage>
            )}
          </Field>
          <Field>
            <Label>Contact Fax</Label>
            <Input
              type="tel"
              value={form.contactFax}
              onChange={(e) => {
                const v = e.currentTarget.value;
                updateForm(['contactFax'], (prev) => ({ ...prev, contactFax: v }));
              }}
              placeholder="(555) 123-4568"
              invalid={!!getFieldError(validationErrors, ['contactFax'])}
            />
            {getFieldError(validationErrors, ['contactFax']) && (
              <ErrorMessage>{getFieldError(validationErrors, ['contactFax'])}</ErrorMessage>
            )}
          </Field>
        </FieldGroup>
      </Fieldset>

      <Fieldset>
        <FieldGroup>
          <Field>
            <Label>Producer Name</Label>
            <Description>
              The authorized representative's name as it appears on documents.
            </Description>
            <Input
              value={form.producerName}
              onChange={(e) => {
                const v = e.currentTarget.value;
                updateForm(['producerName'], (prev) => ({ ...prev, producerName: v }));
              }}
              placeholder="John Doe"
              invalid={!!getFieldError(validationErrors, ['producerName'])}
            />
            {getFieldError(validationErrors, ['producerName']) && (
              <ErrorMessage>{getFieldError(validationErrors, ['producerName'])}</ErrorMessage>
            )}
          </Field>

          <Field>
            <Label>National Producer Number (NPN)</Label>
            <Description>
              This producer's NPN. Filled onto ACORD forms that print a national producer number
              beside the producer's signature.
            </Description>
            <Input
              value={form.nationalProducerNumber}
              onChange={(e) => {
                const v = e.currentTarget.value;
                updateForm(['nationalProducerNumber'], (prev) => ({
                  ...prev,
                  nationalProducerNumber: v
                }));
              }}
              placeholder="12345678"
              invalid={!!getFieldError(validationErrors, ['nationalProducerNumber'])}
            />
            {getFieldError(validationErrors, ['nationalProducerNumber']) && (
              <ErrorMessage>
                {getFieldError(validationErrors, ['nationalProducerNumber'])}
              </ErrorMessage>
            )}
          </Field>

          <Field>
            <Label>FL License Number</Label>
            <Description>
              This producer's Florida license number. Printed onto ACORD forms when the risk is in
              Florida (or the form is Florida-specific), beside the producer's signature.
            </Description>
            <Input
              value={form.floridaLicenseNumber}
              onChange={(e) => {
                const v = e.currentTarget.value;
                updateForm(['floridaLicenseNumber'], (prev) => ({
                  ...prev,
                  floridaLicenseNumber: v
                }));
              }}
              placeholder="A123456"
              invalid={!!getFieldError(validationErrors, ['floridaLicenseNumber'])}
            />
            {getFieldError(validationErrors, ['floridaLicenseNumber']) && (
              <ErrorMessage>
                {getFieldError(validationErrors, ['floridaLicenseNumber'])}
              </ErrorMessage>
            )}
          </Field>

          <Field>
            <Label>Signature</Label>
            <Description>Choose how the producer's signature appears on documents.</Description>
            <div className="space-y-4">
              <RadioGroup
                value={signatureMode}
                onChange={(value) => handleSignatureModeChange(value as 'text' | 'draw')}
              >
                <RadioField>
                  <Radio color="sky" value="text" />
                  <Label>Text (use producer name)</Label>
                </RadioField>
                <RadioField>
                  <Radio color="sky" value="draw" />
                  <Label>Draw signature</Label>
                </RadioField>
              </RadioGroup>

              {signatureMode === 'draw' && (
                <div className="space-y-3">
                  {hasDrawnSignature && !showCanvas ? (
                    <div className="space-y-2">
                      <div className="border border-neutral-200 dark:border-neutral-700 rounded-md overflow-hidden bg-white dark:bg-neutral-800 p-2">
                        <img
                          src={`data:image/jpeg;base64,${form.producerSignatureJpegBase64}`}
                          alt="Saved signature"
                          className="max-h-24 object-contain"
                        />
                      </div>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        Drawn signatures can only be replaced, not edited.
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setForm((prev) => ({ ...prev, producerSignatureJpegBase64: '' }));
                          setShowCanvas(true);
                        }}
                        className="text-xs text-sky-600 dark:text-sky-400 underline hover:text-sky-900 dark:hover:text-sky-200"
                      >
                        Replace signature
                      </button>
                    </div>
                  ) : showCanvas || !hasDrawnSignature ? (
                    <SignatureCanvas
                      onSave={handleSignatureSave}
                      onCancel={() => {
                        if (existingSignatureIsDrawn && initialValues.producerSignatureJpegBase64) {
                          setForm((prev) => ({
                            ...prev,
                            producerSignatureJpegBase64: initialValues.producerSignatureJpegBase64
                          }));
                        }
                        setShowCanvas(false);
                      }}
                    />
                  ) : null}
                </div>
              )}
            </div>
          </Field>
        </FieldGroup>
      </Fieldset>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <div className="flex gap-3 pt-2 pb-6">
        <Button color="sky" type="submit" disabled={loading}>
          {loading ? <Spinner size="size-4" color="white" /> : null}
          Save
        </Button>
        <Button color="white" type="button" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

// ─── Create Page ──────────────────────────────────────────────────────────────

export const ProducerInformationCreatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const submit = useSubmit();
  const navigation = useNavigation();
  const action = useActionData() as
    | ActionResult<CreateAgencyProducerInformationMutation>
    | undefined;

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  const onClose = () => {
    if (location.key === 'default') {
      navigate('/app/settings/agency');
    } else {
      navigate(-1);
    }
  };

  const handleSubmit = (input: ProducerInformationInput) => {
    setValidationErrors([]);
    submit(input, { method: 'POST', encType: 'application/json' });
  };

  const error = action?.applicationError?.message || action?.serverError?.message || null;

  return (
    <MountableSlideout onClose={() => onClose()} title="Create Producer Information">
      <ProducerInformationForm
        initialValues={emptyForm()}
        existingSignatureIsDrawn={false}
        onSubmit={handleSubmit}
        onClose={onClose}
        loading={navigation.state === 'submitting'}
        error={error}
        validationErrors={validationErrors}
      />
    </MountableSlideout>
  );
};

// ─── Edit Page ────────────────────────────────────────────────────────────────

export const ProducerInformationEditPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const submit = useSubmit();
  const navigation = useNavigation();
  const action = useActionData() as ActionResult<UpdateProducerInformationMutation> | undefined;
  const loader = useLoaderData() as
    | { data: Promise<{ producerInformations: ProducerInformationFieldsFragment[] }> }
    | undefined;

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  const onClose = () => {
    if (location.key === 'default') {
      navigate('/app/settings/agency');
    } else {
      navigate(-1);
    }
  };

  const handleSubmit = (input: ProducerInformationInput) => {
    if (!id) return;
    setValidationErrors([]);
    submit(input, { method: 'PUT', encType: 'application/json' });
  };

  const error = action?.applicationError?.message || action?.serverError?.message || null;

  if (!loader) return null;

  return (
    <React.Suspense fallback={null}>
      <Await resolve={loader.data}>
        {(data) => {
          const pi = data?.producerInformations?.find((p) => p.id === id);
          if (!pi) return null;
          const existingSignatureIsDrawn = !!pi.producerSignatureJpegBase64;
          return (
            <MountableSlideout onClose={() => onClose()} title="Edit Producer Information">
              <ProducerInformationForm
                initialValues={piToForm(pi)}
                existingSignatureIsDrawn={existingSignatureIsDrawn}
                onSubmit={handleSubmit}
                onClose={onClose}
                loading={navigation.state === 'submitting'}
                error={error}
                validationErrors={validationErrors}
              />
            </MountableSlideout>
          );
        }}
      </Await>
    </React.Suspense>
  );
};
